import { useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { FileDown, Upload, FileText, FileSpreadsheet, ArrowDownToLine } from "lucide-react";
import { toast } from "sonner";

const EXPORTS = [
  { key: "billing/customer-je", label: "Customer-wise Journal Entries", desc: "Sales register grouped by customer (Excel sheets + Tally Sales vouchers)", icon: FileSpreadsheet, hasDate: true, hasClient: true },
  { key: "billing/collections", label: "Collection / Receipt Entries", desc: "Receipts against invoices (Excel + Tally Receipt vouchers)", icon: ArrowDownToLine, hasDate: true },
  { key: "payroll/employee-je", label: "Employee-wise Payroll JEs", desc: "Monthly salary + PF + ESI + TDS entries per employee (Excel + Tally Journal)", icon: FileSpreadsheet, hasMonth: true },
  { key: "reimbursement/employee-je", label: "Employee-wise Reimbursement JEs", desc: "Approved reimbursement entries per employee (Excel + Tally Journal)", icon: FileSpreadsheet, hasDate: true },
];

const IMPORTS = [
  { key: "clients", label: "Clients", desc: "Bulk client master (name, email, GSTIN, PAN, address)" },
  { key: "vendors", label: "Vendors", desc: "Bulk vendor master" },
  { key: "invoices", label: "Invoices", desc: "Bulk invoice entries with GST + TDS" },
  { key: "employees", label: "Employees", desc: "Employee master with monthly CTC and opening balances. Default password: Welcome@2026" },
  { key: "reimbursements", label: "Reimbursement Claims", desc: "Bulk reimbursement claims by employee email" },
];

export default function FinanceIO() {
  return (
    <div className="space-y-6" data-testid="finance-io-page">
      <div>
        <div className="eyebrow">Office Admin</div>
        <h1 className="font-sans text-3xl">Bulk Exports &amp; Imports</h1>
        <p className="text-sm text-slate-600 mt-1">Generate Tally-ready Excel + XML files for accounting, or bulk-import master data.</p>
      </div>
      <Tabs defaultValue="exports">
        <TabsList>
          <TabsTrigger value="exports" data-testid="tab-exports">Exports</TabsTrigger>
          <TabsTrigger value="imports" data-testid="tab-imports">Imports</TabsTrigger>
        </TabsList>
        <TabsContent value="exports" className="space-y-3">
          {EXPORTS.map((e) => <ExportCard key={e.key} cfg={e}/>)}
        </TabsContent>
        <TabsContent value="imports" className="space-y-3">
          {IMPORTS.map((i) => <ImportCard key={i.key} cfg={i}/>)}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ExportCard({ cfg }) {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [month, setMonth] = useState("");
  const [clientId, setClientId] = useState("");
  const [busy, setBusy] = useState(false);
  const Icon = cfg.icon;

  const download = async (format) => {
    setBusy(true);
    try {
      const params = {};
      if (cfg.hasDate && fromDate) params.from_date = fromDate;
      if (cfg.hasDate && toDate) params.to_date = toDate;
      if (cfg.hasMonth && month) params.month = month;
      if (cfg.hasClient && clientId) params.client_id = clientId;
      const url = `/finance/${cfg.key}/${format}`;
      const { data, headers } = await api.get(url, { params, responseType: "blob" });
      const mime = format === "xlsx" ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" : "application/xml";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([data], { type: mime }));
      const cd = headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m?.[1] || `${cfg.key.replace("/", "_")}.${format === "xlsx" ? "xlsx" : "xml"}`;
      a.click();
      toast.success("Downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-4" data-testid={`export-${cfg.key.replace("/", "-")}`}>
      <div className="flex items-start gap-3">
        <Icon className="h-5 w-5 text-[#C2A671] mt-0.5"/>
        <div className="flex-1">
          <div className="font-semibold">{cfg.label}</div>
          <div className="text-xs text-slate-600">{cfg.desc}</div>
          <div className="flex gap-2 mt-3 items-end flex-wrap">
            {cfg.hasDate && <>
              <div><label className="text-[10px] uppercase tracking-wider text-slate-500">From</label><Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-9 w-36"/></div>
              <div><label className="text-[10px] uppercase tracking-wider text-slate-500">To</label><Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-9 w-36"/></div>
            </>}
            {cfg.hasMonth && <div><label className="text-[10px] uppercase tracking-wider text-slate-500">Month</label><Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 w-36"/></div>}
            <Button variant="outline" onClick={() => download("xlsx")} disabled={busy} data-testid={`xlsx-${cfg.key.replace("/", "-")}`}><FileDown className="h-4 w-4 mr-1"/>Excel</Button>
            <Button variant="outline" onClick={() => download("tally")} disabled={busy} data-testid={`tally-${cfg.key.replace("/", "-")}`}><FileText className="h-4 w-4 mr-1"/>Tally XML</Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

function ImportCard({ cfg }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTpl = async () => {
    try {
      const { data, headers } = await api.get(`/finance/import/${cfg.key}/template`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const cd = headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m?.[1] || `${cfg.key}_template.xlsx`;
      a.click();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/finance/import/${cfg.key}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Imported ${data.inserted} ${cfg.label.toLowerCase()}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); e.target.value = ""; }
  };

  return (
    <Card className="p-4" data-testid={`import-${cfg.key}`}>
      <div className="flex items-start gap-3">
        <Upload className="h-5 w-5 text-[#C2A671] mt-0.5"/>
        <div className="flex-1">
          <div className="font-semibold">{cfg.label}</div>
          <div className="text-xs text-slate-600">{cfg.desc}</div>
          <div className="flex gap-2 mt-3">
            <Button variant="outline" onClick={downloadTpl} data-testid={`tpl-${cfg.key}`}><FileSpreadsheet className="h-4 w-4 mr-1"/>Template</Button>
            <label className={`inline-flex items-center gap-1 px-3 py-2 text-sm border border-slate-300 rounded cursor-pointer hover:bg-slate-50 ${busy ? "opacity-50" : ""}`} data-testid={`upload-${cfg.key}`}>
              <Upload className="h-4 w-4"/>{busy ? "Importing…" : "Upload Filled File"}
              <input type="file" accept=".xlsx,.csv" className="hidden" onChange={upload} disabled={busy}/>
            </label>
          </div>
          {result && <div className="text-xs text-emerald-700 mt-2">✓ Imported {result.inserted} rows {result.default_password ? `(default password set: ${result.default_password})` : ""}</div>}
        </div>
      </div>
    </Card>
  );
}
